import { useCallback, useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ContentfulSearchContent } from '@reward-platform/contentful'
import { useContentfulPreviewMode } from '~/hooks/useContentfulPreviewMode/useContentfulPreviewMode'
import { SearchTabQuery } from '~/schemas/searchPageParams'
import { usePlatformConfig } from '~/context/platform'
import { getSearchPageContent } from '~/services/contentService'
import { ONE_HOUR } from '~/utils/datetime'
import { useLocalisation } from '~/context/localisation'
import { useNotification } from '~/context/notification'
import { GenericClientError } from '~/utils/errors'

const useSearchPageContent = () => {
  const { locale } = useLocalisation()
  const { opCo } = usePlatformConfig()
  const { addErrorCodeNotification } = useNotification()
  const previewMode = useContentfulPreviewMode()

  const {
    data: searchPageData,
    error,
    isError,
    isLoading,
  } = useQuery({
    queryKey: ['searchPage', opCo, locale, previewMode],
    queryFn: () => getSearchPageContent(opCo, locale, previewMode),
    gcTime: ONE_HOUR,
    staleTime: ONE_HOUR,
  })

  useEffect(() => {
    if (isError && error instanceof GenericClientError) {
      addErrorCodeNotification('CONTENT_SEARCH_PAGE_ERROR', {
        type: 'ERROR',
        errorCode: error.code,
        statusCode: error.httpStatus,
      })
    }
  }, [isError, error, addErrorCodeNotification])

  const allTabContent = useMemo(() => searchPageData?.products || [], [searchPageData])
  const tabContentRecord = useMemo<Record<string, ContentfulSearchContent>>(
    () =>
      allTabContent.reduce((acc, tab) => {
        if (tab.product) {
          return { ...acc, [tab.product]: tab }
        }
        return acc
      }, {}),
    [allTabContent]
  )

  const getTabContentByProductSlug = useCallback(
    (slug: SearchTabQuery): ContentfulSearchContent | undefined => tabContentRecord[slug],
    [tabContentRecord]
  )

  return { allTabContent, getTabContentByProductSlug, isLoading }
}

export default useSearchPageContent
